/**
 * Telemetry HTTP client — thin wrappers around the Worker's anonymous-OK
 * ingest endpoints. Used by the global error capture and the page-view
 * effect in main.ts plus any module that wants to record a domain event
 * (e.g. solve finished).
 *
 * Auth: optional. If a Bungie access token is present we send it so the
 * server attributes the event/issue to the user, otherwise the call lands
 * anonymously. Either way we never block on it — fire-and-forget so a
 * down server can never crash the SPA.
 *
 * Rate Limiting: Events are batched and flushed at most once per second.
 * Repeated 429s trigger exponential backoff (up to 60s). Similar events
 * within the batch window are deduplicated.
 */

import { getAccessToken } from '$api/auth';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

function configured(): boolean {
  return API_BASE.length > 0;
}

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const token = await getAccessToken();
    if (token) return { authorization: `Bearer ${token}` };
  } catch {
    // ignore — anonymous fallback
  }
  return {};
}

// ── Event batching & rate limiting ──────────────────────────────────────

export interface EventPayload {
  kind: string;
  path?: string;
  meta?: Record<string, unknown>;
}

interface QueuedEvent extends EventPayload {
  ts: number;
  count: number; // aggregated duplicates
}

const eventQueue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let backoffMs = 0;
let isFlushing = false;

const FLUSH_INTERVAL_MS = 1000; // batch window
const REQUEST_DELAY_MS = 200; // delay between individual requests
const MAX_BACKOFF_MS = 60_000;
const MAX_QUEUE_SIZE = 50;

/** Generate a dedup key for an event. Events with same kind+path within
 *  a batch window are counted once with a `count` field. */
function eventKey(p: EventPayload): string {
  return `${p.kind}|${p.path ?? ''}`;
}

function scheduleFlush(): void {
  if (flushTimer !== null || isFlushing) return;
  const delay = Math.max(FLUSH_INTERVAL_MS, backoffMs);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushEventQueue();
  }, delay);
}

async function flushEventQueue(): Promise<void> {
  if (eventQueue.length === 0 || !configured() || isFlushing) return;
  isFlushing = true;

  try {
    const headers = await authHeaders();

    while (eventQueue.length > 0) {
      const event = eventQueue.shift()!;

      try {
        const res = await fetch(`${API_BASE}/v1/telemetry/event`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...headers },
          body: JSON.stringify({
            kind: event.kind,
            path: event.path,
            meta: { ...event.meta, _count: event.count > 1 ? event.count : undefined },
          }),
          keepalive: true,
        });

        if (res.status === 429) {
          // Backoff: double the delay, cap at MAX_BACKOFF_MS
          backoffMs = Math.min((backoffMs || FLUSH_INTERVAL_MS) * 2, MAX_BACKOFF_MS);
          // Re-queue this event for retry (prepend to preserve order)
          eventQueue.unshift(event);
          break; // stop flushing, wait for backoff
        }

        // Success: reset backoff, small delay before next request
        backoffMs = 0;
        if (eventQueue.length > 0) {
          await new Promise((r) => setTimeout(r, REQUEST_DELAY_MS));
        }
      } catch {
        // Network error — re-queue with mild backoff
        backoffMs = Math.min((backoffMs || FLUSH_INTERVAL_MS) * 1.5, MAX_BACKOFF_MS);
        eventQueue.unshift(event);
        break;
      }
    }
  } finally {
    isFlushing = false;
  }

  // If more events remain (after backoff trigger), schedule another flush
  if (eventQueue.length > 0) {
    scheduleFlush();
  }
}

export async function trackEvent(p: EventPayload): Promise<void> {
  if (!configured()) return;

  const key = eventKey(p);
  const now = Date.now();

  // Dedupe: if an identical event (kind+path) is already queued, bump count
  const existing = eventQueue.find(
    (e) => eventKey(e) === key && now - e.ts < FLUSH_INTERVAL_MS,
  );
  if (existing) {
    existing.count++;
    // Merge meta: for solver_run, aggregate ms/results as arrays or averages
    if (p.meta && existing.meta) {
      // Keep first occurrence's meta, just track the count
    }
    return;
  }

  // Drop if queue is too large (prevent memory leak under sustained load)
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    return;
  }

  eventQueue.push({ ...p, ts: now, count: 1 });
  scheduleFlush();
}

// Flush on page unload to capture final events
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && eventQueue.length > 0) {
      void flushEventQueue();
    }
  });
}

export interface IssuePayload {
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  kind?: 'error' | 'rejection' | 'report';
  buildHash?: string;
}

export async function reportIssue(p: IssuePayload): Promise<void> {
  if (!configured()) return;
  try {
    const headers = await authHeaders();
    await fetch(`${API_BASE}/v1/telemetry/issue`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...headers },
      body: JSON.stringify(p),
      keepalive: true,
    });
  } catch {
    // never propagate
  }
}

// ── error-capture installer ──────────────────────────────────────────────

let installed = false;

/** Install global handlers for window.onerror + onunhandledrejection. We
 *  dedupe within a 2-second window per (message + stack-line) so an
 *  errant render loop doesn't flood the issues table. */
export function installErrorCapture(buildHash?: string): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const seen = new Map<string, number>();
  const DEDUPE_MS = 2000;

  function dedupeKey(message: string, stack: string): string {
    // First line of the stack is usually unique enough for grouping; full
    // stack is too noisy because timestamps and async hops shift it.
    const firstFrame = stack.split('\n').slice(0, 2).join('|');
    return `${message}::${firstFrame}`;
  }

  function shouldSend(key: string): boolean {
    const now = Date.now();
    const last = seen.get(key) ?? 0;
    if (now - last < DEDUPE_MS) return false;
    seen.set(key, now);
    // Cap the map so a long session doesn't grow unbounded.
    if (seen.size > 500) {
      const cutoff = now - 60_000;
      for (const [k, t] of seen) if (t < cutoff) seen.delete(k);
    }
    return true;
  }

  window.addEventListener('error', (ev: ErrorEvent) => {
    const message = ev.message || 'unknown error';
    const stack = ev.error instanceof Error ? (ev.error.stack ?? '') : '';
    if (!shouldSend(dedupeKey(message, stack))) return;
    void reportIssue({
      kind: 'error',
      message,
      stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      buildHash,
    });
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    const reason = ev.reason;
    let message = 'unhandled rejection';
    let stack = '';
    if (reason instanceof Error) {
      message = reason.message || message;
      stack = reason.stack ?? '';
    } else if (typeof reason === 'string') {
      message = reason;
    } else {
      try {
        message = JSON.stringify(reason).slice(0, 480);
      } catch {
        // non-serialisable rejection reason
      }
    }
    if (!shouldSend(dedupeKey(message, stack))) return;
    void reportIssue({
      kind: 'rejection',
      message,
      stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      buildHash,
    });
  });
}
